import Fuse from 'fuse.js'
import rdiff from 'recursive-diff'

import {
    CrisisSupportOption,
    CSOScope,
    SupportedContentBooster,
} from '../entities'

/**
 * Search for 911 alternatives that match the provided query
 * @param data The full list of 911 alternatives to search through
 * @param pattern The search query
 * @returns A list of 911 alternatives that match the search query
 */
export const searchCrisisSupportOptions = (
    data: CrisisSupportOption[],
    pattern: string
): CrisisSupportOption[] => {
    const options = {
        // isCaseSensitive: false,
        // includeScore: false,
        // shouldSort: true,
        // includeMatches: false,
        // findAllMatches: false,
        // minMatchCharLength: 1,
        // location: 0,
        threshold: 0.5,
        // distance: 100,
        // useExtendedSearch: false,
        ignoreLocation: true,
        // ignoreFieldNorm: false,
        keys: [
            {
                name: 'title',
                weight: 5,
            },
            {
                name: 'subheader',
                weight: 4,
            },
            {
                name: 'examples',
                weight: 3,
            },
            {
                name: 'who_they_help',
                weight: 2,
            },
            {
                name: 'details',
                weight: 1,
            },
        ],
    }
    if (pattern.length === 0) return data

    const fuse: Fuse<CrisisSupportOption> = new Fuse(data, options)
    const searchResults: Fuse.FuseResult<CrisisSupportOption>[] =
        fuse.search(pattern)
    return searchResults.map((sr) => sr.item)
}

/**
 * Sort Crisis Support Options
 * @param data The list of Crisis Support Options to sort
 * @param activeFilters The list of active filters
 * @param searchQuery The user-entered search query
 * @returns CrisisSupportOption[]
 */
export const sortCrisisSupportOptions = (
    data: CrisisSupportOption[],
    activeFilters: SupportedContentBooster[],
    searchQuery?: string
): CrisisSupportOption[] => {
    data.sort((a: CrisisSupportOption, b: CrisisSupportOption) => {
        // determine how many filters are toggled on content
        const intersectionA: SupportedContentBooster[] = a.filters.filter((t) =>
            activeFilters.includes(t)
        )
        const intersectionB: SupportedContentBooster[] = b.filters.filter((t) =>
            activeFilters.includes(t)
        )

        if (intersectionA.length !== intersectionB.length) {
            // sort by most filters
            return intersectionB.length - intersectionA.length
        } else {
            // sort by recency of choosing the filter
            for (const filter of activeFilters) {
                const filterInA = intersectionA.includes(filter)
                const filterInB = intersectionB.includes(filter)
                if (filterInA && !filterInB) {
                    return 1
                } else if (!filterInA && filterInB) {
                    return -1
                }
            }
        }
        if (a.scope === CSOScope.LOCAL && b.scope === CSOScope.NATIONAL) {
            return -1
        } else if (
            a.scope === CSOScope.NATIONAL &&
            b.scope === CSOScope.LOCAL
        ) {
            return 1
        } else {
            // peg non-emergency line to the top unless the user has entered a search query
            if (searchQuery && a.priority !== b.priority)
                return a.priority - b.priority
            else return a.title < b.title ? -1 : a.title > b.title ? 1 : 0
        }
    })
    return data
}

/**
 * Takes an old and new state of a crisis support option and replaces old values with new values
 * @param currentCSO the current version of the CSO
 * @param latestCSO the new version of the CSO
 * @returns the updated CSO
 */
export function updateCSO(
    currentCSO: CrisisSupportOption,
    latestCSO: CrisisSupportOption
): CrisisSupportOption {
    if (currentCSO.id !== latestCSO.id) {
        return currentCSO
    }

    const diff: rdiff.rdiffResult[] = rdiff.getDiff(currentCSO, latestCSO)
    const updatedCSO: CrisisSupportOption = rdiff.applyDiff(currentCSO, diff)

    return updatedCSO
}
